import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Bell, X, Users, Ticket, CheckCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDay(dateStr) {
  return new Date(dateStr).toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getInitials(name = "") {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function ChatPage() {
  const { user } = useAuth();
  const { socket, setChatUnread } = useSocket();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const selectedRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Tickets
  const [activeTab, setActiveTab] = useState("convs"); // "convs" | "tickets"
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [closingChat, setClosingChat] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [ticketName, setTicketName] = useState("");

  const [notifModal, setNotifModal] = useState(false);
  const [notifText, setNotifText] = useState("");
  const [notifTarget, setNotifTarget] = useState("all");
  const [notifSent, setNotifSent] = useState(false);

  function sendNotification() {
    if (!notifText.trim() || !socket?.connected) return;
    socket.emit("notification:send", {
      content: notifText.trim(),
      targetUserId: notifTarget === "all" ? null : notifTarget,
    });
    setNotifSent(true);
    setNotifText("");
    setTimeout(() => {
      setNotifSent(false);
      setNotifModal(false);
      setNotifTarget("all");
    }, 1800);
  }

  // Keep selectedRef in sync for use inside socket callbacks
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    if (!socket) return;
    setConnected(socket.connected);

    function onConnect() {
      setConnected(true);
      socket.emit("chat:conversations");
    }
    function onDisconnect() { setConnected(false); }
    function onConversations(data) { setConversations(data); }
    function onHistory(msgs) { setMessages(msgs); }
    function onMessage(msg) {
      const sel = selectedRef.current;
      setMessages((prev) => {
        const belongsHere =
          sel?.id === msg.user_id ||
          (prev.length > 0 && prev[0]?.user_id === msg.user_id);
        if (!belongsHere) return prev;
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      socket.emit("chat:conversations");
    }
    function onUnreadUpdate() { socket.emit("chat:conversations"); }
    function onTickets(data) { setTickets(data); }
    function onTicketHistory(msgs) { setTicketMessages(msgs); }
    function onChatClosed({ ticketId }) {
      setClosingChat(false);
      setSelected(null);
      setMessages([]);
      socket.emit("chat:tickets");
      setActiveTab("tickets");
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("chat:conversations", onConversations);
    socket.on("chat:history", onHistory);
    socket.on("chat:message", onMessage);
    socket.on("chat:unread_update", onUnreadUpdate);
    socket.on("chat:tickets", onTickets);
    socket.on("chat:ticket_history", onTicketHistory);
    socket.on("chat:closed", onChatClosed);

    if (socket.connected) socket.emit("chat:conversations");

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("chat:conversations", onConversations);
      socket.off("chat:history", onHistory);
      socket.off("chat:message", onMessage);
      socket.off("chat:unread_update", onUnreadUpdate);
      socket.off("chat:tickets", onTickets);
      socket.off("chat:ticket_history", onTicketHistory);
      socket.off("chat:closed", onChatClosed);
    };
  }, [socket]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Clear global badge when entering chat page
  useEffect(() => { setChatUnread(0); }, []);

  function selectConversation(conv) {
    setSelected(conv);
    setMessages([]);
    socket?.emit("chat:history", { targetUserId: conv.id });
    socket?.emit("chat:read", { targetUserId: conv.id });
    setConversations((prev) =>
      prev.map((c) => (c.id === conv.id ? { ...c, unread_count: 0 } : c)),
    );
  }

  function sendMessage() {
    const text = input.trim();
    if (!text || !selected || !socket?.connected) return;
    socket.emit("chat:send", { content: text, targetUserId: selected.id });
    setInput("");
  }

  function closeChat() {
    if (!selected || !socket?.connected) return;
    setTicketName("");
    setCloseModal(true);
  }

  function confirmCloseChat() {
    if (!selected) return;
    setCloseModal(false);
    setClosingChat(true);
    socket.emit("chat:close", { targetUserId: selected.id, name: ticketName.trim() || null });
  }

  function selectTicket(ticket) {
    setSelectedTicket(ticket);
    setSelected(null);
    setMessages([]);
    setTicketMessages([]);
    socket?.emit("chat:ticket_history", { ticketId: ticket.id });
  }

  function switchTab(tab) {
    setActiveTab(tab);
    setSelected(null);
    setSelectedTicket(null);
    setMessages([]);
    setTicketMessages([]);
    if (tab === "tickets") socket?.emit("chat:tickets");
    if (tab === "convs") socket?.emit("chat:conversations");
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <>
    <div style={s.page}>
      {/* Header */}
      <div style={s.pageHeader}>
        <h1 style={s.pageTitle}>
          Chat en vivo
          {totalUnread > 0 && (
            <span style={s.unreadBadge}>{totalUnread}</span>
          )}
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button style={s.notifBtn} onClick={() => setNotifModal(true)}>
            <Bell size={15} />
            Enviar aviso
          </button>
          <div style={s.connStatus}>
            <span style={{ ...s.connDot, backgroundColor: connected ? "#16a34a" : "#9a9a93" }} />
            <span style={s.connText}>{connected ? "Conectado" : "Desconectado"}</span>
          </div>
        </div>
      </div>

      <div style={s.layout}>
        {/* Sidebar */}
        <div style={s.sidebar}>
          {/* Tabs */}
          <div style={s.tabs}>
            <button style={{ ...s.tabBtn, ...(activeTab === "convs" ? s.tabBtnActive : {}) }} onClick={() => switchTab("convs")}>
              <MessageCircle size={13} /> Chats
              {totalUnread > 0 && <span style={s.convBadge}>{totalUnread}</span>}
            </button>
            <button style={{ ...s.tabBtn, ...(activeTab === "tickets" ? s.tabBtnActive : {}) }} onClick={() => switchTab("tickets")}>
              <Ticket size={13} /> Tickets
            </button>
          </div>

          {/* Conversations list */}
          {activeTab === "convs" && (
            conversations.length === 0 ? (
              <div style={s.sidebarEmpty}>
                <MessageCircle size={28} color="#d1d5db" />
                <p style={{ margin: "8px 0 0", color: "var(--text-3)", fontSize: 13 }}>Sin conversaciones</p>
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  style={{ ...s.convItem, ...(selected?.id === c.id ? s.convItemActive : {}) }}
                  onClick={() => selectConversation(c)}
                >
                  <div style={s.convAvatar}>{getInitials(c.name)}</div>
                  <div style={s.convInfo}>
                    <div style={s.convName}>{c.name}</div>
                    {c.last_message && (
                      <div style={s.convLast} title={c.last_message}>
                        {c.last_message.length > 35 ? c.last_message.slice(0, 35) + "…" : c.last_message}
                      </div>
                    )}
                  </div>
                  {c.unread_count > 0 && <span style={s.convBadge}>{c.unread_count}</span>}
                </button>
              ))
            )
          )}

          {/* Tickets list */}
          {activeTab === "tickets" && (
            tickets.length === 0 ? (
              <div style={s.sidebarEmpty}>
                <Ticket size={28} color="#d1d5db" />
                <p style={{ margin: "8px 0 0", color: "var(--text-3)", fontSize: 13 }}>Sin tickets</p>
              </div>
            ) : (
              tickets.map((t) => (
                <button
                  key={t.id}
                  style={{ ...s.convItem, ...(selectedTicket?.id === t.id ? s.convItemActive : {}) }}
                  onClick={() => selectTicket(t)}
                >
                  <div style={{ ...s.convAvatar, background: "#f1f5f9", color: "#64748b" }}>
                    <Ticket size={14} />
                  </div>
                  <div style={s.convInfo}>
                    <div style={s.convName}>{t.name || t.user_name || t.user_email}</div>
                    <div style={s.convLast}>
                      #{t.id} · {t.user_name} · {new Date(t.closed_at).toLocaleDateString("es-AR")} · {t.msg_count} msgs
                    </div>
                  </div>
                </button>
              ))
            )
          )}
        </div>

        {/* Chat panel */}
        <div style={s.chatPanel}>
          {!selected && !selectedTicket ? (
            <div style={s.noSelection}>
              <MessageCircle size={48} color="#d1d5db" />
              <p style={s.noSelectionText}>
                {activeTab === "tickets" ? "Seleccioná un ticket para ver el historial" : "Seleccioná una conversación para comenzar"}
              </p>
            </div>

          ) : selectedTicket ? (
            /* ── TICKET VIEW (read-only) ── */
            <>
              <div style={s.chatHeader}>
                <div style={{ ...s.chatAvatar, background: "#f1f5f9", color: "#64748b" }}>
                  <Ticket size={16} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={s.chatHeaderName}>
                    {selectedTicket.name || `Ticket #${selectedTicket.id}`}
                  </div>
                  <div style={s.chatHeaderEmail}>
                    {selectedTicket.user_name} · cerrado el {new Date(selectedTicket.closed_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
                <div style={s.ticketClosedBadge}><CheckCircle size={13} /> Cerrado</div>
              </div>

              <div style={{ ...s.messages }}>
                <div style={s.ticketBanner}>
                  🎫 Ticket #{selectedTicket.id} — {selectedTicket.msg_count} mensajes · {new Date(selectedTicket.created_at).toLocaleDateString("es-AR")} → {new Date(selectedTicket.closed_at).toLocaleDateString("es-AR")}
                </div>
                {ticketMessages.length === 0 ? (
                  <div style={s.messagesEmpty}><MessageCircle size={32} color="#d1d5db" /></div>
                ) : (
                  (() => {
                    let lastDay = null;
                    return ticketMessages.map((msg) => {
                      const isAdmin = msg.sender_role === "admin";
                      const day = formatDay(msg.created_at);
                      const showDay = day !== lastDay;
                      lastDay = day;
                      return (
                        <React.Fragment key={msg.id}>
                          {showDay && <div style={s.dayDivider}><span style={s.dayLabel}>{day}</span></div>}
                          <div style={{ ...s.msgRow, justifyContent: isAdmin ? "flex-end" : "flex-start" }}>
                            {!isAdmin && <div style={s.userAvatar}>{getInitials(selectedTicket.user_name)}</div>}
                            <div style={{ ...s.bubble, ...(isAdmin ? s.bubbleAdmin : s.bubbleUser) }}>
                              <p style={s.bubbleText}>{msg.content}</p>
                              <span style={s.bubbleTime}>{formatTime(msg.created_at)}</span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()
                )}
                <div ref={messagesEndRef} />
              </div>
            </>

          ) : (
            /* ── ACTIVE CONVERSATION ── */
            <>
              {/* Chat header */}
              <div style={s.chatHeader}>
                <div style={s.chatAvatar}>{getInitials(selected.name)}</div>
                <div style={{ flex: 1 }}>
                  <div style={s.chatHeaderName}>{selected.name}</div>
                  <div style={s.chatHeaderEmail}>{selected.email}</div>
                </div>
                <button
                  style={{ ...s.closeChatBtn, opacity: closingChat ? 0.6 : 1 }}
                  onClick={closeChat}
                  disabled={closingChat}
                  title="Finalizar chat y guardar como ticket"
                >
                  {closingChat ? "Finalizando..." : "✓ Finalizar chat"}
                </button>
              </div>

              {/* Messages */}
              <div style={s.messages}>
                {messages.length === 0 ? (
                  <div style={s.messagesEmpty}>
                    <MessageCircle size={32} color="#d1d5db" />
                    <p style={{ margin: "8px 0 0", color: "var(--text-3)", fontSize: 13 }}>
                      Sin mensajes
                    </p>
                  </div>
                ) : (
                  (() => {
                    let lastDay = null;
                    return messages.map((msg) => {
                      const isAdmin = msg.sender_role === "admin";
                      const day = formatDay(msg.created_at);
                      const showDay = day !== lastDay;
                      lastDay = day;
                      return (
                        <React.Fragment key={msg.id}>
                          {showDay && (
                            <div style={s.dayDivider}>
                              <span style={s.dayLabel}>{day}</span>
                            </div>
                          )}
                          <div
                            style={{
                              ...s.msgRow,
                              justifyContent: isAdmin ? "flex-end" : "flex-start",
                            }}
                          >
                            {!isAdmin && (
                              <div style={s.userAvatar}>
                                {getInitials(selected.name)}
                              </div>
                            )}
                            <div
                              style={{
                                ...s.bubble,
                                ...(isAdmin ? s.bubbleAdmin : s.bubbleUser),
                              }}
                            >
                              <p style={s.bubbleText}>{msg.content}</p>
                              <span style={s.bubbleTime}>
                                {formatTime(msg.created_at)}
                                {isAdmin &&
                                  (msg.read_by_user ? " ✓✓" : " ✓")}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={s.inputRow}>
                <textarea
                  style={s.input}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Escribí un mensaje... (Enter para enviar)"
                  rows={1}
                  maxLength={1000}
                />
                <button
                  style={{
                    ...s.sendBtn,
                    ...(!input.trim() || !connected ? s.sendBtnDisabled : {}),
                  }}
                  onClick={sendMessage}
                  disabled={!input.trim() || !connected}
                >
                  <Send size={18} />
                </button>
              </div>
            </>
          )}
          </div>
      </div>
    </div>

    {/* Modal finalizar chat */}
    {closeModal && (
      <div style={s.modalOverlay} onClick={() => setCloseModal(false)}>
        <div style={s.modal} onClick={(e) => e.stopPropagation()}>
          <div style={s.modalHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ ...s.modalIcon, background: "#fee2e2" }}>
                <CheckCircle size={18} color="#dc2626" />
              </div>
              <span style={s.modalTitle}>Finalizar chat</span>
            </div>
            <button style={s.modalClose} onClick={() => setCloseModal(false)}>
              <X size={18} />
            </button>
          </div>
          <div style={s.modalBody}>
            <p style={{ fontSize: 13, color: "var(--text-2)", margin: "0 0 16px" }}>
              Se guardará como ticket vinculado a <strong>{selected?.name}</strong>. El usuario verá que la conversación fue cerrada.
            </p>
            <label style={s.modalLabel}>Nombre del ticket</label>
            <input
              style={{ ...s.modalSelect, marginBottom: 4 }}
              placeholder="Ej: Consulta sobre ruta Norte — Mayo 2026"
              value={ticketName}
              onChange={(e) => setTicketName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmCloseChat()}
              autoFocus
            />
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
              Opcional — si no escribís nada se guardará sin nombre
            </div>
          </div>
          <div style={s.modalFooter}>
            <button style={s.modalCancel} onClick={() => setCloseModal(false)}>Cancelar</button>
            <button
              style={{ ...s.modalSend, background: "#dc2626" }}
              onClick={confirmCloseChat}
            >
              <CheckCircle size={14} />
              Finalizar y guardar ticket
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal de avisos */}
    {notifModal && (
      <div style={s.modalOverlay} onClick={() => setNotifModal(false)}>
        <div style={s.modal} onClick={(e) => e.stopPropagation()}>
          <div style={s.modalHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={s.modalIcon}><Bell size={18} color="#D4620D" /></div>
              <span style={s.modalTitle}>Enviar aviso</span>
            </div>
            <button style={s.modalClose} onClick={() => setNotifModal(false)}>
              <X size={18} />
            </button>
          </div>

          <div style={s.modalBody}>
            <label style={s.modalLabel}>Destinatario</label>
            <select
              style={s.modalSelect}
              value={notifTarget}
              onChange={(e) => setNotifTarget(e.target.value)}
            >
              <option value="all">Todos los usuarios</option>
              {conversations.map((c) => (
                <option key={c.id} value={c.id}>{c.name} — {c.email}</option>
              ))}
            </select>

            <label style={{ ...s.modalLabel, marginTop: 14 }}>Mensaje</label>
            <textarea
              style={s.modalTextarea}
              value={notifText}
              onChange={(e) => setNotifText(e.target.value)}
              placeholder="Escribí el aviso que verán los usuarios en su pantalla..."
              rows={4}
              maxLength={500}
              autoFocus
            />
            <div style={s.modalChars}>{notifText.length}/500</div>
          </div>

          <div style={s.modalFooter}>
            {notifSent ? (
              <div style={s.modalSuccess}>✓ Aviso enviado</div>
            ) : (
              <>
                <button style={s.modalCancel} onClick={() => setNotifModal(false)}>
                  Cancelar
                </button>
                <button
                  style={{ ...s.modalSend, ...(!notifText.trim() ? s.modalSendDisabled : {}) }}
                  onClick={sendNotification}
                  disabled={!notifText.trim()}
                >
                  <Bell size={14} />
                  Enviar aviso
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

const s = {
  page: { width: "100%", display: "flex", flexDirection: "column", height: "calc(100vh - 72px)" },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    flexShrink: 0,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "var(--text)",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  unreadBadge: {
    background: "#D4620D",
    color: "#fff",
    borderRadius: 10,
    padding: "2px 10px",
    fontSize: 15,
    fontWeight: 700,
  },
  connStatus: { display: "flex", alignItems: "center", gap: 6 },
  connDot: { width: 9, height: 9, borderRadius: "50%" },
  connText: { fontSize: 12, color: "var(--text-2)", fontWeight: 600 },

  layout: {
    display: "flex",
    flex: 1,
    gap: 0,
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid var(--border)",
    minHeight: 0,
  },

  // Sidebar
  sidebar: {
    width: 280,
    flexShrink: 0,
    background: "var(--bg-card)",
    borderRight: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  sidebarHeader: {
    padding: "14px 16px",
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text-3)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
  },
  sidebarEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 16px",
  },
  convItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    background: "none",
    border: "none",
    borderBottom: "1px solid var(--border)",
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    fontFamily: "Montserrat, sans-serif",
    transition: "background 0.1s",
  },
  convItemActive: { background: "var(--primary-bg)" },
  convAvatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#FBF0E8",
    color: "#D4620D",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    flexShrink: 0,
  },
  convInfo: { flex: 1, minWidth: 0 },
  convName: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  convLast: {
    fontSize: 11,
    color: "var(--text-3)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginTop: 2,
  },
  convBadge: {
    background: "#D4620D",
    color: "#fff",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 7px",
    flexShrink: 0,
  },

  // Chat panel
  chatPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    background: "#f7f7f5",
    minWidth: 0,
  },
  noSelection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--text-3)",
  },
  noSelectionText: { fontSize: 14, color: "var(--text-3)", marginTop: 12 },

  // Tabs
  tabs: { display: "flex", borderBottom: "1px solid var(--border)", flexShrink: 0 },
  tabBtn: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    padding: "11px 8px", border: "none", background: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 700, color: "var(--text-3)", fontFamily: "inherit",
    borderBottom: "2px solid transparent", transition: "color 0.1s",
  },
  tabBtnActive: { color: "#D4620D", borderBottom: "2px solid #D4620D" },

  // Ticket
  ticketClosedBadge: {
    display: "flex", alignItems: "center", gap: 5,
    background: "#dcfce7", color: "#16a34a",
    borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700,
  },
  ticketBanner: {
    background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "10px 14px", fontSize: 12, color: "#64748b", fontWeight: 600,
    marginBottom: 12, textAlign: "center",
  },
  closeChatBtn: {
    padding: "7px 14px", borderRadius: 8, border: "none",
    background: "#fee2e2", color: "#dc2626",
    fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  chatHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 20px",
    background: "var(--bg-card)",
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
  },
  chatAvatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#FBF0E8",
    color: "#D4620D",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
  },
  chatHeaderName: { fontSize: 14, fontWeight: 700, color: "var(--text)" },
  chatHeaderEmail: { fontSize: 11, color: "var(--text-3)" },

  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  messagesEmpty: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 40,
  },

  dayDivider: { display: "flex", justifyContent: "center", margin: "12px 0" },
  dayLabel: {
    fontSize: 11,
    color: "var(--text-3)",
    background: "var(--bg-subtle)",
    borderRadius: 10,
    padding: "3px 12px",
  },

  msgRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginBottom: 2,
  },
  userAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#FBF0E8",
    color: "#D4620D",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 800,
    flexShrink: 0,
  },
  bubble: {
    maxWidth: "65%",
    borderRadius: 18,
    padding: "10px 14px",
  },
  bubbleUser: {
    background: "#fff",
    borderBottomLeftRadius: 4,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  bubbleAdmin: {
    background: "#D4620D",
    borderBottomRightRadius: 4,
  },
  bubbleText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.5,
    color: "inherit",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleTime: {
    display: "block",
    fontSize: 10,
    marginTop: 4,
    opacity: 0.65,
    textAlign: "right",
  },

  inputRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 10,
    padding: "12px 16px",
    background: "var(--bg-card)",
    borderTop: "1px solid var(--border)",
    flexShrink: 0,
  },
  input: {
    flex: 1,
    resize: "none",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: "10px 14px",
    fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
    color: "var(--text)",
    background: "var(--bg)",
    outline: "none",
    maxHeight: 120,
    lineHeight: 1.5,
  },
  sendBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: "#D4620D",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    flexShrink: 0,
    transition: "opacity 0.1s",
  },
  sendBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },

  notifBtn: {
    display: "flex", alignItems: "center", gap: 7,
    padding: "8px 16px", borderRadius: 10,
    background: "#FBF0E8", border: "1px solid #f4c8a0",
    color: "#D4620D", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },

  // Modal
  modalOverlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9000,
  },
  modal: {
    background: "var(--bg-card)", borderRadius: 18,
    width: "100%", maxWidth: 480,
    boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 20px", borderBottom: "1px solid var(--border)",
  },
  modalIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: "#FBF0E8", display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  modalTitle: { fontSize: 16, fontWeight: 800, color: "var(--text)" },
  modalClose: {
    background: "none", border: "none",
    color: "var(--text-2)", cursor: "pointer", padding: 4,
  },
  modalBody: { padding: "20px 20px 8px" },
  modalLabel: {
    display: "block", fontSize: 12, fontWeight: 700,
    color: "var(--text-2)", marginBottom: 6,
    textTransform: "uppercase", letterSpacing: "0.4px",
  },
  modalSelect: {
    width: "100%", padding: "9px 12px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg)",
    color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif", outline: "none",
  },
  modalTextarea: {
    width: "100%", padding: "10px 12px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg)",
    color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
    resize: "none", outline: "none", lineHeight: 1.5,
    boxSizing: "border-box",
  },
  modalChars: { fontSize: 11, color: "var(--text-3)", textAlign: "right", marginTop: 4 },
  modalFooter: {
    display: "flex", alignItems: "center", justifyContent: "flex-end",
    gap: 10, padding: "16px 20px",
    borderTop: "1px solid var(--border)",
  },
  modalCancel: {
    padding: "9px 18px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg-subtle)",
    color: "var(--text-2)", fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  modalSend: {
    display: "flex", alignItems: "center", gap: 7,
    padding: "9px 20px", borderRadius: 10,
    background: "#D4620D", border: "none",
    color: "#fff", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  modalSendDisabled: { opacity: 0.4, cursor: "not-allowed" },
  modalSuccess: {
    color: "#16a34a", fontWeight: 700, fontSize: 14,
    padding: "8px 0",
  },
};
